import React from 'react';
import { Box, Chip, Stack, Typography, Button, Paper } from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';
import { Intervention, Student } from '@/types';
import { InterventionActions } from './InterventionActions';
import { StudentProfile } from './StudentProfile';

interface InterventionItemProps {
  intervention: Intervention & { student: Student };
  onEdit: () => void;
  onDelete: (id: string) => void;
  onViewDetails: () => void;
}

export const InterventionItem: React.FC<InterventionItemProps> = ({
  intervention,
  onEdit,
  onDelete,
  onViewDetails
}) => {
  const getTypeColor = (type: Intervention['type']) => {
    switch (type) {
      case 'individual':
        return 'primary';
      case 'group':
        return 'success';
      case 'family':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: Intervention['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_process':
        return 'primary';
      case 'frozen':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper 
      elevation={1}
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        opacity: intervention.status === 'completed' ? 0.8 : 1,
        '&:hover': {
          boxShadow: 3,
          bgcolor: 'background.paper',
          opacity: 1
        },
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack spacing={2}>
          {/* Status Section */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={intervention.type}
              color={getTypeColor(intervention.type)}
              size="small"
            />
            <Chip
              label={intervention.status.replace('_', ' ')}
              color={getStatusColor(intervention.status)}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              {new Date(intervention.date).toLocaleDateString()}
            </Typography>
          </Stack>

          {/* Student Profile */}
          <StudentProfile student={intervention.student} />

          {/* Content */}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {intervention.notes}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Outcome:</strong> {intervention.outcome}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 'auto', pt: 2 }}
          >
            <Button
              startIcon={<ViewIcon />}
              variant="contained"
              color="primary"
              onClick={onViewDetails}
              sx={{
                minWidth: 140,
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              View Details
            </Button>
            
            <InterventionActions
              intervention={intervention}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};